import { useCallback, useEffect, useMemo, useState } from "react";
import { Lock, LogOut, Mail, Building2, Clock, Inbox, Loader2, RefreshCw, Search, CheckCheck, Reply, Archive, ArchiveRestore, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "sss_admin_token";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const LoginView = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess();
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#050505]" data-testid="admin-login">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-[#242424] bg-[#0f0f11] p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="grid place-items-center h-10 w-10 rounded-full border border-[#2a2a2a] text-[#2dd4bf]">
            <Lock size={18} />
          </span>
          <div>
            <h1 className="font-display text-xl tracking-tight text-[#eaeaea]">Studio Admin</h1>
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#8b8b8b]">Silver Schnauzer Studios</p>
          </div>
        </div>

        <label className="block mb-5">
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#8b8b8b]">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent border-b border-[#2a2a2a] py-3 text-[#eaeaea] placeholder-[#5a5a5a] outline-none focus:border-[#2dd4bf] transition-colors"
            placeholder="you@studio.com"
            data-testid="admin-email-input"
          />
        </label>

        <label className="block mb-8">
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#8b8b8b]">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b border-[#2a2a2a] py-3 text-[#eaeaea] placeholder-[#5a5a5a] outline-none focus:border-[#2dd4bf] transition-colors"
            placeholder="••••••••"
            data-testid="admin-password-input"
          />
        </label>

        {error && (
          <p className="text-[#f87171] text-sm mb-5" data-testid="admin-login-error">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-[#050505] px-8 py-4 font-mono-accent text-[12px] uppercase tracking-[0.2em] hover:bg-[#eaeaea] transition-colors disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <>Sign In</>}
        </button>
      </form>
    </div>
  );
};

const DATE_FILTERS = [
  { key: "all", label: "All time", days: null },
  { key: "today", label: "Today", days: 1 },
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const InboxView = ({ onLogout }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/contacts`, { headers: authHeaders() });
      if (res.status === 401) {
        onLogout();
        return;
      }
      const data = await res.json();
      setContacts(data.contacts || []);
    } catch {
      setError("Couldn't load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = useCallback(async (id) => {
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, read: true } : c)));
    try {
      await fetch(`${API}/api/admin/contacts/${id}/read`, { method: "POST", headers: authHeaders() });
    } catch {
      /* optimistic — ignore */
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setContacts((prev) => prev.map((c) => ({ ...c, read: true })));
    try {
      await fetch(`${API}/api/admin/contacts/read-all`, { method: "POST", headers: authHeaders() });
    } catch {
      /* optimistic — ignore */
    }
  }, []);

  const toggleArchive = useCallback(async (id) => {
    let next = false;
    setContacts((prev) =>
      prev.map((c) => {
        if (c.id === id) {
          next = !c.archived;
          return { ...c, archived: next };
        }
        return c;
      })
    );
    try {
      await fetch(`${API}/api/admin/contacts/${id}/archive`, { method: "POST", headers: authHeaders() });
    } catch {
      /* optimistic — ignore */
    }
  }, []);

  const removeContact = useCallback(async (id) => {
    if (!window.confirm("Permanently delete this enquiry? This cannot be undone.")) return;
    setContacts((prev) => prev.filter((c) => c.id !== id));
    try {
      await fetch(`${API}/api/admin/contacts/${id}`, { method: "DELETE", headers: authHeaders() });
    } catch {
      /* optimistic — ignore */
    }
  }, []);

  const unread = useMemo(() => contacts.filter((c) => !c.read && !c.archived).length, [contacts]);
  const archivedCount = useMemo(() => contacts.filter((c) => c.archived).length, [contacts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const days = DATE_FILTERS.find((d) => d.key === dateFilter)?.days;
    const cutoff = days ? Date.now() - days * 24 * 60 * 60 * 1000 : null;
    return contacts.filter((c) => {
      if (showArchived ? !c.archived : c.archived) return false;
      if (cutoff && new Date(c.created_at).getTime() < cutoff) return false;
      if (!q) return true;
      return [c.name, c.email, c.company, c.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [contacts, query, dateFilter, showArchived]);

  return (
    <div className="min-h-screen bg-[#050505] text-[#eaeaea]" data-testid="admin-inbox">
      <header className="border-b border-[#1c1c1c] sticky top-0 bg-[#050505]/90 backdrop-blur z-10">
        <div className="mx-auto max-w-[1100px] px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="relative grid place-items-center h-9 w-9 rounded-full border border-[#2a2a2a] text-[#2dd4bf]">
              <Inbox size={16} />
              {unread > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-[#f97362] text-[#050505] text-[10px] font-bold"
                  data-testid="admin-unread-badge"
                >
                  {unread}
                </span>
              )}
            </span>
            <div>
              <h1 className="font-display text-lg tracking-tight">Enquiries</h1>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#8b8b8b]">
                {contacts.length} total{unread > 0 ? ` · ${unread} new` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowArchived((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono-accent text-[11px] uppercase tracking-[0.15em] transition-colors ${
                showArchived
                  ? "border-[#f9803e]/60 text-[#f9803e] bg-[#f9803e]/10"
                  : "border-[#2a2a2a] text-[#bdbdbd] hover:border-[#f9803e]/50 hover:text-[#f9803e]"
              }`}
              data-testid="admin-toggle-archived"
            >
              <Archive size={14} /> {showArchived ? "Inbox" : `Archived${archivedCount ? ` (${archivedCount})` : ""}`}
            </button>
            {unread > 0 && !showArchived && (
              <button
                onClick={markAllRead}
                className="inline-flex items-center gap-2 rounded-full border border-[#2a2a2a] px-4 py-2 font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#bdbdbd] hover:border-[#2dd4bf]/50 hover:text-[#2dd4bf] transition-colors"
                data-testid="admin-mark-all-read"
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
            <button
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-[#2a2a2a] px-4 py-2 font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#bdbdbd] hover:border-[#2dd4bf]/50 hover:text-[#2dd4bf] transition-colors"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full border border-[#2a2a2a] px-4 py-2 font-mono-accent text-[11px] uppercase tracking-[0.15em] text-[#bdbdbd] hover:border-[#f87171]/50 hover:text-[#f87171] transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 py-10">
        {/* Search + date filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5a5a5a]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, company or message…"
              className="w-full bg-[#0f0f11] border border-[#242424] rounded-full pl-11 pr-4 py-3 text-sm text-[#eaeaea] placeholder-[#5a5a5a] outline-none focus:border-[#2dd4bf]/50 transition-colors"
              data-testid="admin-search"
            />
          </div>
          <div className="flex items-center gap-2">
            {DATE_FILTERS.map((d) => (
              <button
                key={d.key}
                onClick={() => setDateFilter(d.key)}
                className={`rounded-full px-4 py-2 font-mono-accent text-[11px] uppercase tracking-[0.15em] border transition-colors ${
                  dateFilter === d.key
                    ? "border-[#2dd4bf]/60 text-[#2dd4bf] bg-[#2dd4bf]/10"
                    : "border-[#242424] text-[#8b8b8b] hover:text-[#bdbdbd]"
                }`}
                data-testid={`admin-date-${d.key}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#8b8b8b] py-20 justify-center">
            <Loader2 size={18} className="animate-spin" /> Loading enquiries…
          </div>
        ) : error ? (
          <p className="text-[#f87171] py-20 text-center">{error}</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-[#8b8b8b]" data-testid="admin-empty">
            <Inbox size={32} className="mx-auto mb-4 opacity-50" />
            {contacts.length === 0 ? "No enquiries yet." : "No enquiries match your filters."}
          </div>
        ) : (
          <ul className="space-y-4">
            {filtered.map((c) => (
              <li
                key={c.id}
                onClick={() => !c.read && markRead(c.id)}
                className={`rounded-2xl border bg-[#0f0f11] p-6 transition-colors ${
                  c.read
                    ? "border-[#1f1f1f] hover:border-[#2dd4bf]/30"
                    : "border-[#2dd4bf]/40 bg-[#0f1615] cursor-pointer hover:border-[#2dd4bf]/60"
                }`}
                data-testid="admin-contact-item"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    {!c.read && (
                      <span
                        className="inline-flex items-center rounded-full bg-[#2dd4bf] text-[#050505] text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5"
                        data-testid="admin-new-badge"
                      >
                        New
                      </span>
                    )}
                    <span className="font-display text-lg tracking-tight">{c.name}</span>
                    <a
                      href={`mailto:${c.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 text-sm text-[#9a9a9a] hover:text-[#2dd4bf] transition-colors"
                    >
                      <Mail size={13} /> {c.email}
                    </a>
                    {c.company && (
                      <span className="inline-flex items-center gap-1.5 text-sm text-[#8b8b8b]">
                        <Building2 size={13} /> {c.company}
                      </span>
                    )}
                  </div>
                  <span className="inline-flex items-center gap-1.5 font-mono-accent text-[11px] text-[#6a6a6a] shrink-0">
                    <Clock size={12} /> {formatDate(c.created_at)}
                  </span>
                </div>
                <p className="text-[#c4c4c4] text-sm leading-relaxed whitespace-pre-wrap">{c.message}</p>

                <div className="mt-5 pt-4 border-t border-[#1a1a1a] flex items-center gap-2 flex-wrap">
                  <a
                    href={`mailto:${c.email}?subject=${encodeURIComponent("Re: Your enquiry — Silver Schnauzer Studios")}&body=${encodeURIComponent(`Hi ${c.name},\n\n`)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 rounded-full bg-gold text-[#050505] px-4 py-2 font-mono-accent text-[10px] uppercase tracking-[0.15em] hover:bg-[#eaeaea] transition-colors"
                    data-testid="admin-reply"
                  >
                    <Reply size={13} /> Reply
                  </a>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleArchive(c.id); }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#2a2a2a] px-4 py-2 font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#bdbdbd] hover:border-[#f9803e]/50 hover:text-[#f9803e] transition-colors"
                    data-testid="admin-archive"
                  >
                    {c.archived ? <><ArchiveRestore size={13} /> Unarchive</> : <><Archive size={13} /> Archive</>}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeContact(c.id); }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#2a2a2a] px-4 py-2 font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#bdbdbd] hover:border-[#f87171]/50 hover:text-[#f87171] transition-colors"
                    data-testid="admin-delete"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));

  useEffect(() => {
    document.title = "Studio Admin — Silver Schnauzer Studios";
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  }, []);

  return authed ? (
    <InboxView onLogout={logout} />
  ) : (
    <LoginView onSuccess={() => setAuthed(true)} />
  );
}
